import React, { FC, RefObject, useCallback, useMemo } from 'react';
import {
  Checkbox,
  Input,
  InputAdornment,
  MenuItem,
  Paper,
  Typography,
} from '@mui/material';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import FilterListIcon from '@mui/icons-material/FilterList';
import { Option } from '../types';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import { formatNumber } from 'src/viewsUtils';

type ControllerProps = {
  selectedOptions: Option[];
  currMax: number;
  label?: string;
  filterValue: string;
  filterRef: RefObject<HTMLDivElement>;
  isDisabled: boolean;
  isOnlyOne: boolean;
  isWithClearSelection: boolean;
  methods: { [key: string]: Function };
  isDense?: boolean;
  isBottomControls?: boolean;
  isHideSelectAll?: boolean;
};

const Controller: FC<ControllerProps> = ({
  selectedOptions,
  currMax,
  label = 'אפשרויות',
  filterValue,
  filterRef,
  isDisabled,
  isOnlyOne,
  isWithClearSelection,
  methods,
  isHideSelectAll = false,
  isBottomControls = false,
  isDense = false,
}) => {
  const {
    setFilterValue,
    onFilterValues,
    onSelectAllToggle,
    onClearSelections,
  } = methods;

  const onSelectToggleClick = useCallback(() => {
    onSelectAllToggle();
  }, [onSelectAllToggle]);
  const onSelectClearClick = useCallback(() => {
    onClearSelections();
  }, [onClearSelections]);

  const onKeyDownCapture = useCallback((event) => {
    event.stopPropagation();
  }, []);

  const onChange = useCallback(
    (event) => {
      setFilterValue(event.target.value);
      onFilterValues(event.target.value);
    },
    [setFilterValue, onFilterValues],
  );

  const startAdornment = useMemo(
    () => (
      <InputAdornment position="end">
        <FilterListIcon
          sx={(theme) => ({
            opacity: 0.8,
            width: isDense ? theme.spacing(2.8125) : theme.spacing(3.125),
            height: isDense ? theme.spacing(2.8125) : theme.spacing(3.125),
          })}
        />
      </InputAdornment>
    ),
    [],
  );

  return (
    <Paper
      sx={(theme) => ({
        borderBottom: !isBottomControls
          ? `1px solid ${theme.palette.dividerLight}`
          : 'none',
        borderTop: isBottomControls
          ? `1px solid ${theme.palette.dividerLight}`
          : 'none',
        background: 'white',
        borderRadius: 0,
        borderTopLeftRadius: !isBottomControls ? theme.spacing(0.5) : 0,
        borderTopRightRadius: !isBottomControls ? theme.spacing(0.5) : 0,
      })}
      elevation={8}
    >
      <Input
        autoFocus
        value={filterValue}
        disabled={isDisabled}
        onKeyDownCapture={onKeyDownCapture}
        onChange={onChange}
        startAdornment={startAdornment}
        sx={(theme) => ({
          width: '100%',
          padding: isDense
            ? theme.spacing(0.75, 0.75, 0.75, 1.125)
            : theme.spacing(0.75, 0.75, 0.75, 1),
          fontSize: isDense ? '0.875rem' : '1rem',
        })}
        placeholder={`סנן ${label.toLowerCase()}`}
        inputRef={filterRef}
      />
      {!isOnlyOne && !isHideSelectAll ? (
        <MenuItem
          sx={{
            padding: '0 0 0 1px',
            marginLeft: 0,
            marginRight: 0,
            width: '100%',
            '&:hover': {
              backgroundColor:
                selectedOptions.length === currMax ? '#fee2dd' : undefined,
            },
          }}
          onClick={onSelectToggleClick}
          disabled={isDisabled}
        >
          <Checkbox
            checked={selectedOptions.length === currMax}
            color="primary"
            checkedIcon={<CheckBoxIcon />}
            sx={{
              padding: ({ spacing }) =>
                isDense ? spacing(0.625) : spacing(1.125),
            }}
            indeterminate={selectedOptions.length !== currMax}
          />
          <Typography
            sx={{
              fontSize: isDense ? '0.8125rem' : '0.875rem',
            }}
          >
            {selectedOptions.length
              ? `${
                  selectedOptions.length === currMax
                    ? 'כל'
                    : `${formatNumber(selectedOptions.length)} מתוך`
                } ${formatNumber(currMax)} נבחרו`
              : 'בחר הכל'}
          </Typography>
        </MenuItem>
      ) : isWithClearSelection ? (
        <MenuItem
          onClick={onSelectClearClick}
          disabled={isDisabled || !Boolean(selectedOptions.length)}
          sx={{
            transition: '0.35s cubic-bezier(.03,.98,.52,.99)',
            display: 'flex',
            gap: ({ spacing }) => spacing(1),
            height: ({ spacing }) => spacing(isDense ? 4 : 5.25),
            padding: ({ spacing }) => spacing(0, 1.125),
            marginLeft: 0,
            marginRight: 0,
            width: '100%',
            '&:hover': {
              backgroundColor: ({ palette }) =>
                Boolean(selectedOptions.length)
                  ? palette.secondary.light
                  : 'transparent',
            },
          }}
        >
          <HighlightOffOutlinedIcon
            sx={{ opacity: 0.75 }}
            color={Boolean(selectedOptions.length) ? 'secondary' : 'disabled'}
          />
          <Typography
            sx={{
              fontSize: isDense ? '0.8125rem' : '0.875rem',
            }}
          >
            אפס בחירה
          </Typography>
        </MenuItem>
      ) : null}
    </Paper>
  );
};

export default Controller;
